import { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { FileSignature, Upload, Loader2, FileText } from "lucide-react";

export default function OrderConfirm({ leadId, contract, defaultPrezzo, onDone }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [prezzo, setPrezzo] = useState(defaultPrezzo || "");
  const [data, setData] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);

  const confirmed = !!(contract?.archived || contract?.ordine_confermato);

  const viewDoc = async () => {
    const isIOS = /iP(hone|ad|od)/.test(navigator.userAgent) || (/Macintosh/.test(navigator.userAgent) && "ontouchend" in document);
    const iosTab = isIOS ? window.open("", "_blank") : null;
    try {
      const res = await api.get(`/leads/${leadId}/order-confirmation/document`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      if (isIOS) { if (iosTab) iosTab.location.href = url; else window.location.href = url; }
      else window.open(url, "_blank");
    } catch (e) { if (iosTab) iosTab.close(); toast.error("Documento non disponibile"); }
  };

  const submit = async () => {
    if (!file) { toast.error("Seleziona il documento firmato (PDF o immagine)"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (prezzo) fd.append("prezzo_finale", String(prezzo));
      if (data) fd.append("data_contratto", data);
      await api.post(`/leads/${leadId}/order-confirmation`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Conferma d'ordine creata · lead spostato a Contratto");
      setOpen(false);
      onDone && onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (confirmed) {
    if (!contract?.signed_document) return null;
    return (
      <div className="mt-3 flex flex-wrap items-center gap-2 no-print" data-testid="order-confirm-done">
        <Button size="sm" variant="outline" onClick={viewDoc} data-testid="view-signed-doc-btn">
          <FileText className="h-4 w-4 mr-2" /> Vedi documento firmato
        </Button>
      </div>
    );
  }

  return (
    <div className="mt-3 no-print" data-testid="order-confirm">
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="order-confirm-btn">
        <FileSignature className="h-4 w-4 mr-2" /> Conferma d'ordine (documento firmato)
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md bg-popover" data-testid="order-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Crea conferma d'ordine</DialogTitle>
            <DialogDescription>Carica il documento firmato manualmente dal cliente (PDF o immagine). Il lead passerà allo stato Contratto.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Documento firmato</Label>
              <Input type="file" accept="application/pdf,image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="order-confirm-file" className="mt-1" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Data contratto</Label>
              <Input type="date" value={data} onChange={(e) => setData(e.target.value)} data-testid="order-confirm-date" className="mt-1 max-w-[180px]" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Prezzo finale (IVA incl.)</Label>
              <Input type="number" value={prezzo} onChange={(e) => setPrezzo(e.target.value)} placeholder="es. 18500" data-testid="order-confirm-prezzo" className="mt-1 max-w-[180px]" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={submit} disabled={saving} className="font-semibold" data-testid="order-confirm-submit">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />} Crea conferma d'ordine
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
